'use client'

import { useEffect, useState } from 'react'

import { isChunkLoadError, reloadOnChunkError } from '@/utils/chunkErrors'

/**
 * Global error boundary — catches errors thrown in the root layout itself, so it
 * must render its own <html>/<body> (the root layout is replaced). Kept minimal
 * and dependency-free (providers may be the thing that failed).
 *
 * Stale-chunk failures are recovered by a single hard reload onto the current
 * build (see `@/utils/chunkErrors`) instead of surfacing an error.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const [reloading, setReloading] = useState(() => isChunkLoadError(error))

  useEffect(() => {
    if (isChunkLoadError(error)) {
      setReloading(reloadOnChunkError(error))
    }
  }, [error])

  if (reloading) {
    // A hard reload is scheduled; show a neutral, text-free loader (this
    // boundary runs without i18n) to avoid an error flash before navigation.
    return (
      <html lang="fr">
        <body
          style={{
            margin: 0,
            display: 'flex',
            minHeight: '100vh',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span
            aria-label="Loading"
            role="status"
            style={{
              width: '2rem',
              height: '2rem',
              border: '3px solid rgba(0,0,0,0.15)',
              borderTopColor: 'rgba(0,0,0,0.55)',
              borderRadius: '50%',
              animation: 'chunk-reload-spin 0.8s linear infinite',
            }}
          />
          <style>
            {'@keyframes chunk-reload-spin{to{transform:rotate(360deg)}}'}
          </style>
        </body>
      </html>
    )
  }

  return (
    <html lang="fr">
      <body
        style={{
          fontFamily: 'system-ui, sans-serif',
          padding: '2rem',
          margin: 0,
        }}
      >
        <h2>Une erreur critique est survenue</h2>
        <p style={{ opacity: 0.7 }}>{error.message}</p>
        <button type="button" onClick={() => reset()}>
          Réessayer
        </button>
      </body>
    </html>
  )
}
